import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Popper,
  List,
  ListItemButton,
  Typography,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useUnifiedSearch } from '../hooks/useItemSearch';
import { colors } from '../constants';

/**
 * Debounced product/ingredient search field with a results dropdown, an
 * optional barcode-scan button, and selected/new-item banners.
 *
 * Memoized: on a form with many line items, editing one field must not
 * re-render (and re-serialize the styles of) every other line's search box.
 * Callers must therefore pass stable callbacks.
 */
function ProductSearchInput({
  value,
  onChangeText,
  onItemSelected,
  productType = 'all',
  placeholder = 'Scan or search by barcode, name, or SKU...',
  label,
  isRequired,
  selectedItem,
  suggestion,
  onUseSuggestion,
  onClear,
  onBarcodeScan,
  showNoMatch = false,
  // Off for a search box that only adds existing items, where typed text that
  // matches nothing does not become a new product.
  showNewItemHint = true,
  // When set, a search where no result has the typed text as a barcode offers
  // an "Add item" option that hands the typed text back to the caller.
  onCreateNew,
  // Exposes the underlying <input> so callers can focus a specific line, e.g.
  // right after adding a new one.
  inputRef,
}) {
  const [text, setText] = useState(value || '');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  // The results list is portalled and anchored here, so it is not clipped when
  // the field sits inside a scrolling container such as the line items table.
  const anchorRef = useRef(null);

  useEffect(() => setText(value || ''), [value]);

  const { data: results, isLoading } = useUnifiedSearch(
    debounced,
    productType,
    open && debounced.length >= 1
  );

  const handleChange = (val) => {
    setText(val);
    onChangeText(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebounced(val);
      setOpen(val.trim().length >= 1);
    }, 300);
  };

  const handleSelect = (item) => {
    setText(item.sku);
    onChangeText(item.sku);
    setOpen(false);
    onItemSelected(item);
  };

  const handleCreateNew = () => {
    setOpen(false);
    onCreateNew(searchedCode);
  };

  const searchedCode = debounced.trim();
  const showCreateNew =
    Boolean(onCreateNew) &&
    open &&
    !isLoading &&
    Array.isArray(results) &&
    searchedCode.length > 0 &&
    !results.some((item) => item.barcodes?.includes(searchedCode));
  const showResults = open && (results?.length ?? 0) > 0;

  const handleClear = () => {
    setText('');
    onChangeText('');
    setDebounced('');
    setOpen(false);
    if (onClear) onClear();
  };

  return (
    <Box sx={{ position: 'relative', mb: 1 }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: colors.gray700 }}>
          {label}
          {isRequired && <span style={{ color: colors.error }}> *</span>}
        </Typography>
      )}
      <Stack direction="row" spacing={1} ref={anchorRef}>
        <TextField
          fullWidth
          size="small"
          inputRef={inputRef}
          value={text}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => text.trim().length >= 1 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <CircularProgress size={16} />
            ) : text ? (
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />
        {onBarcodeScan && (
          <IconButton
            onClick={onBarcodeScan}
            sx={{
              backgroundColor: colors.primary,
              color: '#fff',
              borderRadius: 1.5,
              '&:hover': { backgroundColor: colors.primaryDark },
            }}
          >
            <QrCodeScannerIcon />
          </IconButton>
        )}
      </Stack>

      {selectedItem ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <CheckCircleIcon sx={{ color: colors.success, fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: colors.accent }}>
            {selectedItem.name} ({selectedItem.sku}) - Stock: {selectedItem.stock}{' '}
            {selectedItem.unit}
          </Typography>
        </Stack>
      ) : suggestion && !open ? (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onClick={onUseSuggestion}
          sx={{
            mt: 0.5,
            p: 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            backgroundColor: '#f2f7ff',
            border: `1px solid ${colors.primary}`,
          }}
        >
          <CompareArrowsIcon sx={{ color: colors.primary, fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: colors.primary, fontWeight: 500, flex: 1 }}>
            Same name exists: {suggestion.name} ({suggestion.sku}) — tap to link
          </Typography>
          <ChevronRightIcon sx={{ color: colors.primary, fontSize: 16 }} />
        </Stack>
      ) : showNewItemHint && (showNoMatch || text.trim().length > 0) && !open ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <AddCircleOutlineIcon sx={{ color: colors.warning, fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: colors.warning }}>
            No matching product - will be created as new
          </Typography>
        </Stack>
      ) : null}

      {(showResults || showCreateNew) && (
        <Popper
          open
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              maxHeight: 260,
              overflow: 'auto',
            }}
          >
            <List dense disablePadding>
              {showCreateNew && (
                <ListItemButton
                  sx={showResults ? { borderBottom: `1px solid ${colors.gray200}` } : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreateNew}
                >
                  <AddCircleOutlineIcon sx={{ color: colors.primary, fontSize: 18, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 600 }} noWrap>
                    Add item &quot;{searchedCode}&quot;
                  </Typography>
                </ListItemButton>
              )}
              {showResults && results.map((item) => (
                <ListItemButton
                  key={`${item.type}-${item.uuid}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 600 }}>
                      {item.sku}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {item.name}
                    </Typography>
                    {item.barcodes?.length > 0 && (
                      <Typography variant="caption" sx={{ color: colors.gray500 }} noWrap>
                        Barcode: {item.barcodes.join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Typography variant="caption" sx={{ color: colors.gray600 }}>
                      {item.stock} {item.unit}
                    </Typography>
                    <Chip size="small" label={item.type} variant="outlined" />
                  </Stack>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      )}
    </Box>
  );
}

export default memo(ProductSearchInput);
